import React, { Fragment, useState, useEffect } from 'react';
import CircularProgress from '@material-ui/core/CircularProgress';
import PropTypes from 'prop-types';
import humps from 'humps';
import toastr from 'toastr';
import http from '../../shared/http';
import ProgressBar from '../../shared/ProgressBar';
import { useInterval, useIsMounted } from '../../hooks';
import './VideoUpload.scss';

import { VIDEO_STATUS } from '../../shared/status_descriptions';

const DEFAULT_POLLING_TIME = 10000;

const VideoSpecs = () => (
  <div className="specs video-specs">
    <p className="spec-header">VIDEO SPECIFICATIONS</p>
    <span className="spec-container">
      <p className="spec-label">Resolution:</p>
      <p className="spec-value">720p quality (HD preferred)</p>
    </span>
    <span className="spec-container">
      <p className="spec-label">Format:</p>
      <p className="spec-value">.mov or .mp4</p>
    </span>
    <span className="spec-container">
      <p className="spec-label">File size:</p>
      <p className="spec-value">up to 1GB</p>
    </span>
  </div>
);

const UploadButton = ({ onFileUpload }) => (
  <label className="btn btn-simple" type="button">
    Upload New Video
    <input
      accept="video/*"
      className="video-upload-input"
      style={{ display: 'none' }}
      type="file"
      onChange={onFileUpload}
      data-testid="upload-button"
    />
  </label>
);

UploadButton.propTypes = {
  onFileUpload: PropTypes.func.isRequired
};

const ProgressMessage = ({ message }) => (
  <div className="ml2">{message} <CircularProgress size={20} /></div>
);

ProgressMessage.propTypes = {
  message: PropTypes.string.isRequired
};

const VideoUpload = props => {
  const { pollingTime } = props;
  const [s3Credentials, setS3Credentials] = useState({});
  const [uploadPercent, setUploadPercent] = useState(0);
  const [filename, setFilename] = useState(
    props.CreativeService.creative.mediaUrl &&
      props.CreativeService.creative.mediaUrl.match(/([^/]+$)/)[0]
  );
  const [videoStatus, setVideoStatus] = useState(
    props.CreativeService.creative.status
  );

  const isMounted = useIsMounted();

  const isComplete = videoStatus === 'complete';
  const isFailed = videoStatus === 'error' || videoStatus === 'captioning-failed';
  const shouldPoll = filename && !isComplete && !isFailed;

  useEffect(() => {
    if (isFailed) {
      toastr.error('Something went wrong, please try again!');
    }
  }, [isFailed]);

  useInterval(() => {
    http
      .get(`/creative_previews/${props.CreativeService.creative.id}/video_status`)
      .then(({ data }) => {
        if (!isMounted()) {
          return;
        }
        if (data.status === 'complete') {
          videoPostProcessing();
          setUploadPercent(0);
        } else {
          setVideoStatus(data.status);
        }
      });
  }, shouldPoll ? pollingTime : null);

  useEffect(() => {
    http.get('/creative_previews/s3_credentials').then(({ data }) => {
      if (!isMounted()) {
        return;
      }
      setS3Credentials({
        folder: data.folder,
        bucket: data.bucket,
        policy: data.credentials.policy,
        signature: data.credentials.signature,
        access_key_id: data.credentials.key
      });
    });
  }, []);

  const setMediaUrl = url => {
    props.CreativeService.set('mediaUrl', url);
  };

  const handleUploadProgress = progressEvent => {
    setUploadPercent(
      parseInt((100.0 * progressEvent.loaded) / progressEvent.total, 10)
    );
  };

  const handleFileUpload = event => {
    const file = event.target.files[0];
    let fileName = file.name.replace(/[^\w,.=:-]/g, '');
    setFilename(fileName);
    setVideoStatus('uploading');

    const formParams = {
      key: `${s3Credentials.folder}/${fileName.replace(/\s/g, '')}`,
      acl: 'private',
      AWSAccessKeyId: s3Credentials.access_key_id,
      policy: s3Credentials.policy,
      signature: s3Credentials.signature,
      file: file
    };
    http
      .postForm(
        `https://${s3Credentials.bucket}.s3.amazonaws.com/`,
        formParams,
        handleUploadProgress
      )
      .then(data => {
        setMediaUrl(`${data.config.url}${data.config.data.get('key')}`);
        props.CreativeService.set('status', 'processing'); // FIXME: this is backend business
        props.SaveCreativeService.save(props.CreativeService.creative);
      })
      .catch(() => setVideoStatus('error'));
  };

  const videoPostProcessing = () => {
    // Update rendered creative with data from video processing
    http
      .get(`/creative_previews/${props.CreativeService.creative.id}.json`)
      .then(({ data }) => {
        props.CreativeService.setVideoAttributes(humps.camelizeKeys(data));
        setVideoStatus('complete');
      })
      .catch(() => setVideoStatus('error'));
  };

  const UploadControls = () => {
    const status = VIDEO_STATUS[videoStatus];
    const description = status ? status.description : videoStatus;

    switch (videoStatus) {
      case 'uploading':
        return (
          <ProgressBar
            completedPercentage={uploadPercent}
            filename={filename}
            status={videoStatus}
          />
        );
      case 'processing':
      case 'captions-pending':
      case 'captioning':
        return (
          <ProgressMessage message={description} />
        );
      case 'captioning-failed':
        return (
          <Fragment>
            <UploadButton onFileUpload={handleFileUpload} />
            <div className="ml2">{description}</div>
          </Fragment>
        );
      case 'complete':
        return (
          <Fragment>
            <UploadButton onFileUpload={handleFileUpload} />
            <strong className="ml2">{filename}</strong>
          </Fragment>
        );
      default:
        return (
          <Fragment>
            <UploadButton onFileUpload={handleFileUpload} />
            <VideoSpecs />
          </Fragment>
        );
    }
  };

  return (
    <div className="video-upload-container mt2 mb2">
      <UploadControls />
    </div>
  );
};

VideoUpload.propTypes = {
  pollingTime: PropTypes.number,
  SaveCreativeService: PropTypes.shape({
    save: PropTypes.func.isRequired
  }).isRequired,
  CreativeService: PropTypes.shape({
    set: PropTypes.func.isRequired,
    creative: PropTypes.object.isRequired,
    setVideoAttributes: PropTypes.func.isRequired
  }).isRequired
};

VideoUpload.defaultProps = {
  pollingTime: DEFAULT_POLLING_TIME
};

export default VideoUpload;
